/**
 * 鼠标改键 — SDK 数据转换层
 *
 * 职责：
 * - SDK keyMap → UI KeySetting（回显：parseLayerMapToKeySettings）
 * - UI KeySetting → SDK payload（下发：buildSdkKeyMapPayloadFromSetting）
 * - UI payload → KeySetting（用户操作：buildKeySettingFromPayload）
 * - 恢复默认（buildRestoreKeyMapPayload）
 *
 * 内部分为两大处理路径：
 * 1. Code 映射类（单键/多媒体/鼠标键/DPI/功能键/滚轮/禁用）
 *    通过 KEYCODE_META_MAP 的 type+code 查询完成双向转换。
 * 2. 结构化类（组合键/宏/任意组合）
 *    直接存取 SDK 结构化数据（combinKey/code/macro），不走 code 查询。
 *    其中"预定义组合键快捷方式"（keycode.json type=15）可反查出 target，
 *    "用户自定义组合键"则 target 为 null。
 */

import type { LayoutKey, MouseKeys } from '@rapoo/device-sdk'
import i18n from '@/locales'
import type { KeyChangePayload, KeySetting, KeyType } from '../types'
import keycodeData from '@/utils/keycode.json'
import { KeyConfig } from '@rapoo/element'
import type { KeycodeLabel, ResolveMappedMetaResult } from '../types'
import {
  SdkKeyAttrType,
  KeycodeMetaType,
  SDK_TO_META_MAP,
  META_TO_SDK_MAP,
  DEFAULT_MOUSE_KEYWORDS_BY_INDEX,
  DEFAULT_MOUSE_INDEX_BY_KEYWORDS,
  MOUSE_KEY_TO_CODE,
  CODE_TO_MOUSE_BIT,
} from './constants'

// ============ 工具函数 =================

const tk = (key: string, named?: Record<string, unknown>) =>
  (named ? i18n.global.t(key, named) : i18n.global.t(key)) as string

/**
 * 解析 number/string（支持 0x 前缀）为 number。
 */
export const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value
  if (typeof value !== 'string' || value === '') return undefined
  const parsed = Number.parseInt(value, 0)
  return Number.isNaN(parsed) ? undefined : parsed
}

const getOS = () => {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('windows')) return 'windows'
  if (ua.includes('macintosh') || ua.includes('mac os')) return 'macx'
  if (ua.includes('linux')) return 'linux'
  return null
}

export const getLocalizedField = (field: string | KeycodeLabel = '') => {
  if (!field) return ''
  if (typeof field === 'string') return field
  const os = getOS()
  if (os && field[os]) return field[os]!
  return field.standard || ''
}

// ============ Keycode 元数据索引 =================

/** 统一索引 key：`${type}_${code}` */
const makeLookupKey = (type: number, code: number) => `${type}_${code}`

const KEYCODE_ITEMS = ((keycodeData as { key_code_default?: { key?: KeyConfig[] } })
  ?.key_code_default?.key ?? []) as KeyConfig[]

/** keycode 元数据索引：keywords → KeyConfig */
export const KEYCODE_KEYWORDS_MAP = new Map<string, KeyConfig>()

/** keycode 元数据索引：type_code → KeyConfig */
const KEYCODE_META_MAP = new Map<string, KeyConfig>()

for (const item of KEYCODE_ITEMS) {
  const type = parseNumber(item.type)
  const code = parseNumber(item.code)
  if (type == null || code == null) continue
  KEYCODE_META_MAP.set(makeLookupKey(type, code), item)
  KEYCODE_KEYWORDS_MAP.set(item.keywords, item)
}

// ============ 内部辅助 =================

type MouseLayoutKeys = LayoutKey[] | LayoutKey[][]

type MouseMappedMetaResult = Omit<ResolveMappedMetaResult, 'keyType'> & {
  keyType: Extract<KeyType, 'normal' | 'media' | 'keyboard'>
}

const normalizeLayoutKeys = (layoutKeys?: MouseLayoutKeys): LayoutKey[] => {
  if (!layoutKeys?.length) return []
  return Array.isArray(layoutKeys[0])
    ? (layoutKeys as LayoutKey[][]).flat()
    : (layoutKeys as LayoutKey[])
}

const resolveCodeValue = (value: unknown): number | undefined => {
  if (Array.isArray(value)) return parseNumber(value[0])
  return parseNumber(value)
}

const resolveMouseCode = (targetMouseKey?: MouseKeys.mouseKey) => {
  const bitIndex = targetMouseKey?.findIndex((v: number) => v === 1) ?? -1
  return bitIndex >= 0 ? MOUSE_KEY_TO_CODE[bitIndex] : undefined
}

/** SDK wheel.direction → keycode.json code（0x00→0x01 上滚/右滚，0x01→0xff 下滚/左滚） */
const wheelDirectionToCode = (direction?: number) => {
  if (direction === 0x00) return 0x01
  if (direction === 0x01) return 0xff
  return direction
}

/** keycode.json code → SDK wheel.direction（0x01 上滚/右滚→0x00，0xff 下滚/左滚→0x01） */
const codeToWheelDirection = (code?: number): MouseKeys.WheelDirection => {
  if (code === 0x01) return 0x00
  if (code === 0xff) return 0x01
  return code as MouseKeys.WheelDirection
}

/** 通过 item.index 在 layoutKeys 中查找对应的原始物理按键 */
const resolveOriginalKey = (
  item: MouseKeys.keyMapItem,
  layoutKeys?: MouseLayoutKeys,
): LayoutKey | undefined => {
  const originalKey = normalizeLayoutKeys(layoutKeys).find((key) => key.key === item.index)
  if (originalKey) return originalKey

  const keywords = DEFAULT_MOUSE_KEYWORDS_BY_INDEX[item.index]
  if (!keywords) return undefined
  return { key: item.index, keywords }
}

const resolveKeyType = (type?: number): MouseMappedMetaResult['keyType'] => {
  if (type === 0x01) return 'keyboard'
  return 'media'
}

/** 通过 type+code 在 KEYCODE_META_MAP 中查找映射目标元数据 */
const resolveMappedMeta = ({
  type,
  code,
}: {
  type?: number
  code?: number
}): MouseMappedMetaResult | undefined => {
  if (type == null || code == null) return undefined
  const keyMeta = KEYCODE_META_MAP.get(makeLookupKey(type, code))
  if (!keyMeta) return undefined
  return { keywords: keyMeta.keywords, keyType: resolveKeyType(type) }
}

const resolveOriginalLabel = (originalKey: LayoutKey) => {
  const config = KEYCODE_KEYWORDS_MAP.get(originalKey.keywords)
  return getLocalizedField(config?.alias || config?.name || originalKey.keywords)
}

const createBaseRaw = (item: MouseKeys.keyMapItem, originalKey: LayoutKey) => ({
  index: item.index,
  originalLabel: resolveOriginalLabel(originalKey),
  type: item.keyAttr.type,
})

const resolveSettingIndex = (setting?: KeySetting): number | undefined => {
  const index = parseNumber(setting?.raw?.index)
  if (index != null) return index
  const fallback = DEFAULT_MOUSE_INDEX_BY_KEYWORDS[setting?.keywords || '']
  return typeof fallback === 'number' ? fallback : undefined
}

// ============ 回显：SDK keyMap → UI KeySetting =================

/**
 * 把设备返回的 keyMap 转为 UI 需要的 keySettings。
 *
 * 解析策略：
 * 1. 遍历 keyMapItem，通过 item.index 找到原始物理按键；
 * 2. 根据 keyAttr.type 分发到不同处理分支：
 *    - Default(0xff)：跳过
 *    - Code 映射类：通过 KEYCODE_META_MAP 查询判断是否改键
 *    - 结构化类（组合键/宏）：直接生成改键 setting
 * 3. 同一物理按键只保留最后一次有效映射。
 */
export const parseMapToKeySettings = ({
  keyMap,
  layoutKeys,
}: {
  keyMap: MouseKeys.keyMapItem[]
  layoutKeys?: MouseLayoutKeys
}): KeySetting[] => {
  const settingsByKey = new Map<string, KeySetting>()

  keyMap.forEach((item) => {
    const originalKey = resolveOriginalKey(item, layoutKeys)
    if (!originalKey?.keywords) return

    const targetType = parseNumber(item.keyAttr.type) as SdkKeyAttrType | undefined
    if (targetType == null || targetType === SdkKeyAttrType.Default) return

    const mapping = SDK_TO_META_MAP.get(targetType)
    if (!mapping) return

    let setting: KeySetting | undefined

    if (mapping.structured) {
      setting = parseStructuredKey(targetType, item, originalKey)
    } else {
      setting = parseCodeMappedKey(targetType, item, originalKey, mapping)
    }

    if (setting) {
      settingsByKey.set(originalKey.keywords, setting)
    }
  })

  return Array.from(settingsByKey.values())
}

// ---------- 回显：结构化类型解析 ----------

/** structured 解析结构化类型（组合键/宏/任意组合） */
const parseStructuredKey = (
  targetType: SdkKeyAttrType,
  item: MouseKeys.keyMapItem,
  originalKey: LayoutKey,
): KeySetting | undefined => {
  switch (targetType) {
    case SdkKeyAttrType.CombinedKey:
      return parseCombinedKey(item, originalKey)
    case SdkKeyAttrType.Macro:
      return parseMacroKey(item, originalKey)
    case SdkKeyAttrType.ArbitraryCombined:
      return makeStructuredSetting(item, originalKey, 'combinedKey', {
        code: item.keyAttr.code,
      })
    default:
      return undefined
  }
}

/**
 * 解析组合键回显。
 *
 * 先尝试通过 combinKey+code 反查 keycode.json type=15 的预定义快捷键，
 * 若命中则设置 target 为对应 KeyConfig（回显时能正确显示 label）。
 * 若未命中则为用户自定义组合键，target 设为 null。
 *
 * 两种情况 keyType 统一为 'combinedKey'，通过 target 是否为 null 区分。
 */
const parseCombinedKey = (
  item: MouseKeys.keyMapItem,
  originalKey: LayoutKey,
): KeySetting | undefined => {
  const code = item.keyAttr.code
  const combinKey = item.keyAttr.combinKey

  // 尝试反查预定义快捷键
  if (code?.length && combinKey) {
    const keyCode = code[0]
    const metaKey = KEYCODE_META_MAP.get(makeLookupKey(KeycodeMetaType.CombinedKey, keyCode))
    if (metaKey) {
      const modifierIndex = combinKey.findIndex((v: number) => v === 1)
      const expectedMCode = modifierIndex >= 0 ? 0xe0 + modifierIndex : undefined
      const actualMCode = parseNumber(metaKey.mCode)

      if (expectedMCode != null && expectedMCode === actualMCode) {
        return {
          keywords: originalKey.keywords,
          isModified: true,
          keyType: 'combinedKey',
          target: metaKey as KeySetting['target'],
          raw: {
            ...createBaseRaw(item, originalKey),
            code,
            combinKey,
            mCode: metaKey.mCode,
          },
        }
      }
    }
  }

  // 用户自定义组合键
  return makeStructuredSetting(item, originalKey, 'combinedKey', { code, combinKey })
}

/** 解析宏回显 */
const parseMacroKey = (
  item: MouseKeys.keyMapItem,
  originalKey: LayoutKey,
): KeySetting | undefined => {
  if (!item.keyAttr.macro) return undefined
  const macroIndex = item.keyAttr.macro.index
  return makeStructuredSetting(item, originalKey, 'macro', {
    macro: item.keyAttr.macro,
    macroName: `${tk('keys.macro.label')} ${macroIndex ?? ''}`.trim(),
  })
}

/**
 * 从 code 数组和 combinKey 位数组反查按键名，拼接为 "Ctrl+Shift+G" 形式的 label。
 */
const buildCombinedLabel = (code?: number[], combinKey?: MouseKeys.combinKey): string => {
  const labels: string[] = []

  // 修饰键名称（按 combinKey 位索引）
  const MODIFIER_NAMES = ['LCtrl', 'LShift', 'LAlt', 'LWin', 'RCtrl', 'RShift', 'RAlt', 'RWin']

  // 从 combinKey 位数组提取修饰键名
  if (combinKey) {
    for (let i = 0; i < combinKey.length; i++) {
      if (combinKey[i] === 1) {
        labels.push(MODIFIER_NAMES[i] || `Mod${i}`)
      }
    }
  }

  // 从 code 数组反查普通键名
  if (code) {
    for (const c of code) {
      if (c === 0) continue
      // 在 keycode.json type=1（Keyboard）中查找
      const keyMeta = KEYCODE_META_MAP.get(makeLookupKey(KeycodeMetaType.Keyboard, c))
      if (keyMeta) {
        labels.push(getLocalizedField(keyMeta.alias || keyMeta.name))
      } else {
        labels.push(`0x${c.toString(16).toUpperCase()}`)
      }
    }
  }

  return labels.join('+')
}

/** 生成结构化类型的 KeySetting（target=null），对应 自定义组合键/宏这类改键 */
const makeStructuredSetting = (
  item: MouseKeys.keyMapItem,
  originalKey: LayoutKey,
  mode: Extract<KeyType, 'macro' | 'combinedKey'>,
  extraRaw: Record<string, unknown>,
): KeySetting => {
  const raw: Record<string, unknown> = { ...createBaseRaw(item, originalKey), ...extraRaw }

  // 自定义组合键：从 code + combinKey 反查按键名生成 combinedLabel
  if (mode === 'combinedKey') {
    raw.combinedLabel = buildCombinedLabel(
      extraRaw.code as number[] | undefined,
      extraRaw.combinKey as MouseKeys.combinKey | undefined,
    )
  }

  return {
    keywords: originalKey.keywords,
    isModified: true,
    keyType: mode,
    target: null,
    raw,
  }
}

// ---------- 回显：Code 映射类型解析 ----------

/** 解析 code 映射类型（单键/多媒体/鼠标键/DPI/功能键/滚轮/禁用） */
const parseCodeMappedKey = (
  targetType: SdkKeyAttrType,
  item: MouseKeys.keyMapItem,
  originalKey: LayoutKey,
  mapping: { metaTypes: KeycodeMetaType[] },
): KeySetting | undefined => {
  const candidates = buildMetaCandidates(targetType, item, mapping)
  if (!candidates.length) return undefined

  // 在 KEYCODE_META_MAP 中查找第一个命中的候选
  let mapped: MouseMappedMetaResult | undefined
  let mappedConfig: KeyConfig | undefined

  for (const candidate of candidates) {
    mapped = resolveMappedMeta(candidate)
    mappedConfig = KEYCODE_KEYWORDS_MAP.get(mapped?.keywords || '')
    if (mapped && mappedConfig) break
  }

  if (!mapped || !mappedConfig) return undefined

  // 映射目标和原按键功能一致 → 未改键
  if (mapped.keywords === originalKey.keywords) return undefined

  const setting: KeySetting = {
    keywords: originalKey.keywords,
    isModified: true,
    keyType: mapped.keyType,
    target: mappedConfig as KeySetting['target'],
    raw: { ...createBaseRaw(item, originalKey), icon: mapped.icon ?? '' },
  }

  // 功能键额外保存 snipingFire 数据
  if (targetType === SdkKeyAttrType.Function) {
    setting.raw.snipingFire = item.keyAttr.snipingFire
  }

  return setting
}

/**
 * 根据 SDK type 构造 KEYCODE_META_MAP 查询候选。
 * 不同 SDK type 从 keyAttr 中取 code 的方式不同。
 */
const buildMetaCandidates = (
  sdkType: SdkKeyAttrType,
  item: MouseKeys.keyMapItem,
  mapping: { metaTypes: KeycodeMetaType[] },
): { type?: number; code?: number }[] => {
  switch (sdkType) {
    case SdkKeyAttrType.Keyboard:
      return [{ type: KeycodeMetaType.Keyboard, code: item.keyAttr.code?.[0] }]

    case SdkKeyAttrType.MouseButton:
      return [
        {
          type: KeycodeMetaType.MouseButton,
          code: resolveMouseCode(item.keyAttr.mouseKey?.mouseKey),
        },
      ]

    case SdkKeyAttrType.Media:
      return mapping.metaTypes.map((metaType) => ({ type: metaType, code: item.keyAttr.code?.[0] }))

    case SdkKeyAttrType.Disable:
      return [{ type: KeycodeMetaType.Disable, code: 0x00 }]

    case SdkKeyAttrType.DPI:
      return [{ type: KeycodeMetaType.DPI, code: item.keyAttr.dpi?.type }]

    case SdkKeyAttrType.Function:
      return [{ type: KeycodeMetaType.Function, code: item.keyAttr.snipingFire?.type }]

    case SdkKeyAttrType.DIY:
      return [{ type: KeycodeMetaType.DIY, code: item.keyAttr.diy?.type }]

    case SdkKeyAttrType.VerticalWheel:
      return [
        {
          type: KeycodeMetaType.WheelVertical,
          code: wheelDirectionToCode(item.keyAttr.wheel?.direction),
        },
      ]

    case SdkKeyAttrType.HorizontalWheel:
      return [
        {
          type: KeycodeMetaType.WheelHorizontal,
          code: wheelDirectionToCode(item.keyAttr.wheel?.direction),
        },
      ]

    default:
      return []
  }
}

// ============ 下发：UI KeySetting → SDK payload =================

/**
 * 把 UI 层的 KeySetting 转为 SDK 可接受的 keyMapItem。
 *
 * 处理顺序：
 * 1. 宏 → 直接构造 macro payload
 * 2. 组合键（target 为 null）→ 用户自定义组合键，从 raw 构造
 * 3. 组合键（target 非 null）→ 预定义快捷键，从 target 的 mCode+code 构造
 * 4. 其余 → 通过 META_TO_SDK_MAP 反查 SDK type，按类型构造 payload
 */
export const buildSdkKeyMapPayloadFromSetting = (
  setting: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  const index = resolveSettingIndex(setting)
  if (index == null) return undefined

  const payload: MouseKeys.keyMapItem = { index, keyAttr: { type: 0x00 } }

  // 宏
  if (setting.keyType === 'macro') {
    return buildMacroPayload(payload, setting)
  }

  // 组合键
  if (setting.keyType === 'combinedKey') {
    return buildCombinedKeyPayload(payload, setting)
  }

  // Code 映射类型
  return buildCodeMappedPayload(payload, setting)
}

// ---------- 下发：宏 ----------

const buildMacroPayload = (
  payload: MouseKeys.keyMapItem,
  setting: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  if (!setting.raw?.macro) return undefined
  payload.keyAttr = { type: SdkKeyAttrType.Macro, macro: setting.raw.macro }
  return payload
}

// ---------- 下发：组合键 ----------

/**
 * 构造组合键 payload。
 * - 有 target（预定义快捷键）：从 target.mCode + target.code 构造
 * - 无 target（用户自定义）：从 raw.combinKey + raw.code 构造
 */
const buildCombinedKeyPayload = (
  payload: MouseKeys.keyMapItem,
  setting: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  // 预定义快捷键：有 target 且 target 中有 mCode
  const target = setting.target as KeyConfig | undefined
  if (target && target.mCode) {
    return buildPresetCombinedKeyPayload(payload, target)
  }

  // 用户自定义组合键：从 raw 中取 combinKey 和 code
  return buildCustomCombinedKeyPayload(payload, setting)
}

/** 预定义快捷键（type=15）→ SDK payload */
const buildPresetCombinedKeyPayload = (
  payload: MouseKeys.keyMapItem,
  target: KeyConfig,
): MouseKeys.keyMapItem | undefined => {
  const code = parseNumber(target.code)
  const mCode = parseNumber(target.mCode)
  if (code == null || mCode == null) return undefined

  const modifierIndex = mCode - 0xe0
  if (modifierIndex < 0 || modifierIndex > 7) return undefined

  const combinKey: MouseKeys.combinKey = [0, 0, 0, 0, 0, 0, 0, 0]
  combinKey[modifierIndex] = 1

  payload.keyAttr = {
    type: SdkKeyAttrType.CombinedKey,
    code: [code, 0],
    combinKey,
  }
  return payload
}

/** 用户自定义组合键 → SDK payload */
const buildCustomCombinedKeyPayload = (
  payload: MouseKeys.keyMapItem,
  setting: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  const type =
    parseNumber(setting.raw?.type) ??
    (setting.raw?.combinKey ? SdkKeyAttrType.CombinedKey : SdkKeyAttrType.ArbitraryCombined)
  const code = Array.isArray(setting.raw?.code) ? setting.raw.code : undefined
  if (!code?.length) return undefined

  payload.keyAttr = { type, code }

  if (type === SdkKeyAttrType.CombinedKey) {
    if (!setting.raw?.combinKey) return undefined
    payload.keyAttr.combinKey = setting.raw.combinKey
  }

  return payload
}

// ---------- 下发：Code 映射类型 ----------

/** 通过 META_TO_SDK_MAP 反查 SDK type，按类型构造 payload */
const buildCodeMappedPayload = (
  payload: MouseKeys.keyMapItem,
  setting: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  const target = setting.target as KeyConfig | undefined
  const metaType = parseNumber(target?.type ?? setting.raw?.type) as KeycodeMetaType | undefined
  const code = resolveCodeValue(target?.code ?? setting.raw?.code)
  if (metaType == null) return undefined

  // 默认键：查找原始按键元数据并恢复
  if (metaType === KeycodeMetaType.Default) {
    return buildRestoreDefaultPayload(payload.index, setting.keywords)
  }

  const sdkType = META_TO_SDK_MAP.get(metaType)
  if (sdkType == null) return undefined

  return buildPayloadByType(payload, sdkType, code, setting)
}

/** 根据 SDK type 构造具体的 keyAttr payload */
const buildPayloadByType = (
  payload: MouseKeys.keyMapItem,
  sdkType: SdkKeyAttrType,
  code: number | undefined,
  setting: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  switch (sdkType) {
    case SdkKeyAttrType.Disable:
      payload.keyAttr.type = SdkKeyAttrType.Disable
      return payload

    case SdkKeyAttrType.Keyboard:
      if (code == null) return undefined
      payload.keyAttr = { type: SdkKeyAttrType.Keyboard, code: [code] }
      return payload

    case SdkKeyAttrType.Media:
      if (code == null) return undefined
      payload.keyAttr = { type: SdkKeyAttrType.Media, code: [code] }
      return payload

    case SdkKeyAttrType.CombinedKey: {
      // 从 code 映射路径进入（target.type=15），构造预定义快捷键 payload
      if (code == null) return undefined
      const target = setting.target as KeyConfig | undefined
      const mCode = parseNumber(target?.mCode ?? setting.raw?.mCode)
      if (mCode == null) return undefined

      const modifierIndex = mCode - 0xe0
      if (modifierIndex < 0 || modifierIndex > 7) return undefined

      const combinKey: MouseKeys.combinKey = [0, 0, 0, 0, 0, 0, 0, 0]
      combinKey[modifierIndex] = 1

      payload.keyAttr = { type: SdkKeyAttrType.CombinedKey, code: [code, 0], combinKey }
      return payload
    }

    case SdkKeyAttrType.MouseButton: {
      if (code == null) return undefined
      const bitIndex = CODE_TO_MOUSE_BIT[code]
      if (bitIndex == null) return undefined

      const mouseKey: MouseKeys.mouseKey = [0, 0, 0, 0, 0]
      mouseKey[bitIndex] = 1

      payload.keyAttr = {
        type: SdkKeyAttrType.MouseButton,
        mouseKey: { mouseKey, isDouble: Boolean(setting.raw?.mouseKey?.isDouble) },
      }
      return payload
    }

    case SdkKeyAttrType.VerticalWheel:
    case SdkKeyAttrType.HorizontalWheel: {
      if (code == null) return undefined
      const direction = codeToWheelDirection(code)
      if (direction == null) return undefined

      payload.keyAttr = {
        type: sdkType,
        wheel: { type: 0xff, valueType: 0xff as MouseKeys.WheelValueType, direction },
      }
      return payload
    }

    case SdkKeyAttrType.DPI:
      if (code == null) return undefined
      payload.keyAttr = { type: SdkKeyAttrType.DPI, dpi: { type: code as MouseKeys.DPIType } }
      return payload

    case SdkKeyAttrType.Function: {
      if (code == null) return undefined
      const fireType = parseNumber(setting.raw?.snipingFire?.fireType ?? setting.raw?.fireType)
      payload.keyAttr = {
        type: SdkKeyAttrType.Function,
        snipingFire: {
          type: code as MouseKeys.SnipingFireType,
          fireType: (fireType ?? 0) as MouseKeys.FireType,
        },
      }
      return payload
    }

    case SdkKeyAttrType.DIY:
      if (code == null) return undefined
      payload.keyAttr = { type: SdkKeyAttrType.DIY, diy: { type: code as MouseKeys.DIYType } }
      return payload

    default:
      return undefined
  }
}

// ============ UI 操作：payload → KeySetting =================

/**
 * 把 UI 改键 payload 统一为 KeySetting 结构。
 */
export const buildKeySettingFromPayload = ({
  sourceData,
  index,
}: {
  sourceData: KeyChangePayload
  index: number
}): KeySetting | undefined => {
  if (!sourceData?.keywords) return undefined

  return {
    keywords: sourceData.keywords,
    isModified: true,
    keyType: sourceData.keyType,
    target: sourceData.target,
    raw: {
      index,
      type: sourceData.raw?.type,
      code: sourceData.raw?.code,
      icon: sourceData.raw?.icon || '',
      originalLabel: getLocalizedField((sourceData.target as KeyConfig)?.alias || ''),
      combinKey: sourceData.raw?.combinKey,
      combinedLabel: sourceData.raw?.combinedLabel,
      mCode: sourceData.raw?.mCode,
      mouseKey: sourceData.raw?.mouseKey,
      macro: sourceData.raw?.macro,
      macroName: sourceData.raw?.macroName,
      snipingFire: sourceData.raw?.snipingFire,
      fireType: sourceData.raw?.fireType,
    },
  }
}

// ============ 恢复默认 =================

/**
 * 根据原始按键的 keywords 查找其 keycode.json 元数据，
 * 构造恢复默认键位的 SDK payload（写入原始按键的 type + code）。
 *
 * 替代之前直接发送 type=0xff 的错误做法 —— 0xff 会导致按键失去原本功能。
 */
const buildRestoreDefaultPayload = (
  index: number,
  keywords: string,
): MouseKeys.keyMapItem | undefined => {
  const originalKeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)
  const originalMetaType = parseNumber(originalKeyConfig?.type) as KeycodeMetaType | undefined
  const originalCode = resolveCodeValue(originalKeyConfig?.code)

  if (originalMetaType == null || originalCode == null) return undefined

  const sdkType = META_TO_SDK_MAP.get(originalMetaType)
  if (sdkType == null) return undefined

  const payload: MouseKeys.keyMapItem = { index, keyAttr: { type: 0x00 } }

  // 构造虚拟 setting 供 buildPayloadByType 构造各类型 payload
  const restoreSetting: KeySetting = {
    keywords,
    isModified: false,
    keyType: 'normal',
    target: originalKeyConfig as KeySetting['target'],
    raw: {},
  }

  return buildPayloadByType(payload, sdkType, originalCode, restoreSetting)
}

/**
 * 生成恢复默认键位的 SDK payload。
 */
export const buildRestoreKeyMapPayload = (
  setting?: KeySetting,
): MouseKeys.keyMapItem | undefined => {
  const index = resolveSettingIndex(setting)
  if (index == null) return undefined
  if (!setting?.keywords) return undefined
  return buildRestoreDefaultPayload(index, setting.keywords)
}
